import {
    getItems,
    getLocalStorageItem,
    objectLang,
    setLocalStorageItem,
    getAuthHeader,
    loadChildrenFolders,
    getImagePath
} from "../functions"
import React, {useCallback, useContext, useEffect, useState} from "react"
import {LinksClass} from "../components/LinksClass"
import {WindowPopup} from "../components/WindowPopup"
import {AuthContext} from "../context/AuthContext"
import config from "../config.json"
import {useHttp} from "../hooks/http.hook"

export const CloudinaryUploadImagePage = () => {
    setLocalStorageItem("page", "CloudinaryUploadImagePage")

    const auth = useContext(AuthContext)
    const lang = objectLang().language
    const { request } = useHttp()

    const [dragActive, setDragActive] = useState(false)
    const [rootFolderId, setRootFolderId] = useState(auth.rootFolder)

    // МУЛЬТИЗАГРУЗКА
    const [files, setFiles] = useState([])
    const [fileNames, setFileNames] = useState([])

    const [tree, setTree] = useState({})
    const [selectedFolder, setSelectedFolder] = useState({
        id: auth.rootFolder,
        name: lang.body.myLinksPage.title
    })

    const [uploading, setUploading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [result, setResult] = useState(null)
    const [error, setError] = useState(null)
    const [currentIndex, setCurrentIndex] = useState(0)

    const host = window.location.host === "localhost:3000"
        ? config.localHost
        : config.onrender

    // -----------------------------
    // ДОБАВЛЕНИЕ ФАЙЛОВ
    // -----------------------------
    const addFiles = newFiles => {
        const images = newFiles.filter(f => f.type.startsWith("image/"))
        const limited = [...files, ...images].slice(0, 20)

        setFiles(limited)
        setFileNames(limited.map(f => f.name))
    }

    // -----------------------------
    // DRAG & DROP
    // -----------------------------
    const handleDrop = e => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (!e.dataTransfer.files) return
        addFiles(Array.from(e.dataTransfer.files))
    }

    const handleDrag = e => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true)
        else if (e.type === "dragleave") setDragActive(false)
    }

    // -----------------------------
    // INPUT MULTIPLE
    // -----------------------------
    const handleFileChange = e => {
        addFiles(Array.from(e.target.files))
    }

    // -----------------------------
    // handleToggle — ДЕРЕВО ПАПОК
    // -----------------------------
    const getParentIds = (tree, node) => {
        const parents = []
        let current = node

        while (current.parentId) {
            const parent = tree[current.parentId]
            if (!parent) break
            parents.push(parent.id)
            current = parent
        }
        return parents
    }

    const handleToggle = async node => {
        setSelectedFolder({ id: node.id, name: node.name })

        if (!node.loaded) {
            if (getLocalStorageItem(node.id)) {
                const localRes = getLocalStorageItem(node.id)
                loadChildrenFolders(localRes, node.id, setTree)
            } else {
                await loadChildren(node.id)
            }
        }

        setTree(prev => {
            const parentIds = getParentIds(prev, node)
            const newTree = {}

            Object.values(prev).forEach(n => {
                newTree[n.id] = {
                    ...n,
                    expanded: n.id === node.id || parentIds.includes(n.id)
                }
            })

            return newTree
        })
    }

    // -----------------------------
    // ЗАГРУЗКА ФАЙЛОВ ПО ОЧЕРЕДИ
    // -----------------------------
    const handleUpload = async () => {
        if (files.length === 0) return

        const maxSize = 10 * 1024 * 1024

        for (const f of files) {
            if (f.size > maxSize) {
                setError(`Файл ${f.name} превышает 10 МБ`)
                return
            }
        }

        setUploading(true)
        setError(null)
        setProgress(0)

        const filesToUpload = [...files]
        const namesToUpload = [...fileNames]

        let uploadedCount = 0

        for (let i = 0; i < filesToUpload.length; i++) {
            const file = filesToUpload[i]
            const fileName = namesToUpload[i]

            const imagePathName = getImagePath(
                getLocalStorageItem(selectedFolder.id),
                auth
            )

            setCurrentIndex(i)

            await new Promise(resolve => {
                const formData = new FormData()
                formData.append("file", file)
                formData.append("name-image-file", encodeURIComponent(fileName))
                formData.append("date-created", Date.now().toString())
                formData.append("image-path-name", imagePathName)

                const xhr = new XMLHttpRequest()
                xhr.open("POST", `${host}/api/cloudinary/single-image-upload`)
                xhr.withCredentials = true
                xhr.setRequestHeader("Authorization", getAuthHeader(auth))

                xhr.upload.addEventListener("progress", e => {
                    if (e.lengthComputable) {
                        const percent = Math.round((e.loaded / e.total) * 100)
                        const totalPercent = Math.round(
                            ((uploadedCount + percent / 100) / filesToUpload.length) * 100
                        )
                        setProgress(totalPercent)
                    }
                })

                xhr.addEventListener("loadend", () => {
                    uploadedCount++
                    resolve()
                })

                xhr.send(formData)
            })
        }

        setUploading(false)
        setResult({ count: filesToUpload.length })
    }

    // -----------------------------
    // ДЕРЕВО ПАПОК (инициализация)
    // -----------------------------
    const loadChildren = useCallback(async (parentId = rootFolderId) => {
        try {
            setTree(prev => ({
                ...prev,
                [parentId]: { ...prev[parentId], loading: true }
            }))

            const res = await request(
                `${host}/api/folder/open/${parentId}`,
                "GET",
                null,
                {
                    Authorization: getAuthHeader(auth),
                    "Content-Type": "application/json"
                }
            )

            setLocalStorageItem(parentId, res)
            return loadChildrenFolders(res, parentId, setTree)

        } catch {
            setTree(prev => ({
                ...prev,
                [parentId]: { ...prev[parentId], loading: false, parentId }
            }))
            return []
        }
    }, [request, auth, host, rootFolderId])

    useEffect(() => {
        setRootFolderId(auth.rootFolder)

        const initTree = async () => {
            const currentFolder = getLocalStorageItem("current-folder-tree") || { 0: rootFolderId }
            const folderIds = Object.values(currentFolder)
            const lastFolderId = folderIds[folderIds.length - 1]

            setTree({
                [rootFolderId]: {
                    id: rootFolderId,
                    name: objectLang().language.body.myLinksPage.title,
                    children: [],
                    loaded: false,
                    expanded: true,
                    loading: false,
                    parentId: ""
                }
            })

            let parentId = rootFolderId

            for (const folderId of folderIds.slice(1)) {
                if (getLocalStorageItem(parentId)) {
                    const localRes = getLocalStorageItem(parentId)
                    loadChildrenFolders(localRes, parentId, setTree)
                } else {
                    await loadChildren(parentId)
                }
                parentId = folderId
                setSelectedFolder(prev => ({ ...prev, id: folderId }))
            }

            if (getLocalStorageItem(lastFolderId)) {
                const localRes = getLocalStorageItem(lastFolderId)
                loadChildrenFolders(localRes, lastFolderId, setTree)
            } else {
                await loadChildren(lastFolderId)
            }
        }

        initTree().then()
    }, [loadChildren, auth, rootFolderId])

    return (
        <>
            <LinksClass
                auth={auth}
                documents={getLocalStorageItem('documents')}
                items={getItems(getLocalStorageItem('documents'))}
                rootFolder={auth.rootFolder}
            />

            <WindowPopup
                data={{
                    tree,
                    rootFolderId,
                    selectedFolder,
                    uploading,
                    progress,
                    result,
                    error,

                    files,
                    fileNames,
                    setFileNames,

                    handleToggle,
                    handleFileChange,
                    handleUpload,
                    handleDrag,
                    handleDrop,
                    dragActive,
                    currentIndex,

                    typeData: 'cloudinary-upload-image',
                }}
                lang={lang}
                title={'Загрузка Картинок'}
            />
        </>
    )
}
